import type { LifeSpace, Project, Section } from '../db/models'
import { normalizeSearchText, searchWords } from '../search/matchSearch'
import type { MoveTarget } from './moveTasks'

/*
  **Verschieben** (#263, CONTEXT.md): Was das Panel an der Zeile zeigt — reine Rechnung, ohne React.
  Oben die **Lebensbereich-Wahl**, darunter die Projekte mit ihren Abschnitten; beim Tippen die
  Treffer aller Lebensbereiche. Gesucht wird wie in der **Suche** (`lib/search`): alle Wörter müssen
  vorkommen, ohne Akzente und Groß/klein.
*/

/** In der Reihenfolge der Seitenleiste. */
export const MOVE_LIFE_SPACES: readonly LifeSpace[] = ['personal', 'work', 'home']

export interface MoveTargetProject {
  project: Project
  depth: number
  /** Namen der Eltern, oberstes zuerst — die Suche findet ein Unterprojekt auch über sie. */
  ancestors: string[]
  sections: Section[]
}

/** Alle Ziele je Lebensbereich, so wie `useMoveDestinations` sie beim Öffnen lädt. */
export type MoveTargets = ReadonlyMap<LifeSpace, readonly MoveTargetProject[]>

/** Wo die Aufgabe heute steht. `null` bei mehreren Gewählten — die können von überall kommen. */
export type MoveCurrent = { projectId: string | null; sectionId: string | null } | null

export interface MoveRow {
  key: string
  kind: 'project' | 'section'
  lifeSpace: LifeSpace
  project: Project
  section: Section | null
  depth: number
  /** Der heutige Platz: mit Haken, gesperrt. */
  here: boolean
  /** Ein Projekt, das nur wegen eines Abschnitts oder Unterprojekts dasteht — wählbar bleibt es trotzdem. */
  context: boolean
  target: MoveTarget
  /** Für die Meldung: „Garten“ oder „Garten / Beete“. */
  place: string
}

/** Projekte in der Ordnung der Seitenleiste, Unterprojekte eingerückt hinter ihrem Projekt. */
export function projectRows(projects: readonly Project[]): Array<{ project: Project; depth: number; ancestors: string[] }> {
  // Archivierte stehen nicht mehr in der Seitenleiste — wie im Wecken-Menü (`MoveProjectDropdown`).
  const live = projects.filter(project => !project.isSystem && !project.deletedAt && project.status !== 'archived')
  const ids = new Set(live.map(project => project.id))
  const parentOf = (project: Project) => (project.parentId && ids.has(project.parentId) ? project.parentId : null)
  const rows: Array<{ project: Project; depth: number; ancestors: string[] }> = []
  const walk = (parentId: string | null, ancestors: string[]) => {
    for (const project of live.filter(entry => parentOf(entry) === parentId).sort((a, b) => a.order - b.order)) {
      rows.push({ project, depth: ancestors.length, ancestors })
      walk(project.id, [...ancestors, project.name])
    }
  }
  walk(null, [])
  return rows
}

/** Abschnitte, die eine Liste zeigt: mit Titel, nicht archiviert. */
export const shownSections = (sections: readonly Section[]): Section[] =>
  sections.filter(section => !section.deletedAt && !section.archivedAt && section.title.trim() !== '').sort((a, b) => a.order - b.order)

/** Die **Lebensbereich-Wahl**: der Lebensbereich der Aufgabe zuerst, die anderen wie in der Seitenleiste. */
export function moveSpaceOrder(first: LifeSpace): LifeSpace[] {
  return [first, ...MOVE_LIFE_SPACES.filter(lifeSpace => lifeSpace !== first)]
}

const matches = (text: string, words: readonly string[]) => {
  const normalized = normalizeSearchText(text)
  return words.every(word => normalized.includes(word))
}

/**
 * Die Zeilen eines Lebensbereichs. Mit Suche: Ein Projekt-Treffer bringt alle seine Abschnitte mit,
 * ein Abschnitt-Treffer sein Projekt, ein Unterprojekt seine Eltern — als Zusammenhang. Die Wörter
 * dürfen sich auf Eltern, Projekt und Abschnitt verteilen („garten bee“).
 */
export function moveRows(targets: MoveTargets, lifeSpace: LifeSpace, query: string, current: MoveCurrent): MoveRow[] {
  const list = targets.get(lifeSpace) ?? []
  const words = searchWords(query)
  const searching = words.length > 0

  const hits = list.map(({ project, ancestors, sections }) => {
    const path = [...ancestors, project.name].join(' ')
    const own = !searching || matches(path, words)
    const matched = own ? sections : sections.filter(section => matches(`${path} ${section.title}`, words))
    return { own, sections: matched }
  })
  // Eltern eines Treffers stehen als Zusammenhang da: rückwärts, denn Kinder stehen hinter ihren Eltern.
  const shown = hits.map(hit => hit.own || hit.sections.length > 0)
  for (let i = list.length - 1; i >= 0; i--) {
    if (!shown[i]) continue
    for (let j = i - 1, depth = list[i]!.depth; j >= 0 && depth > 0; j--) {
      if (list[j]!.depth < depth) {
        shown[j] = true
        depth = list[j]!.depth
      }
    }
  }

  const rows: MoveRow[] = []
  list.forEach(({ project, depth }, i) => {
    if (!shown[i]) return
    const here = current?.projectId === project.id
    rows.push({
      key: `p:${project.id}`,
      kind: 'project',
      lifeSpace,
      project,
      section: null,
      depth,
      here: here && current?.sectionId == null,
      context: !hits[i]!.own,
      target: { projectId: project.id, sectionId: null, lifeSpace },
      place: project.name,
    })
    for (const section of hits[i]!.sections) {
      rows.push({
        key: `s:${section.id}`,
        kind: 'section',
        lifeSpace,
        project,
        section,
        depth: depth + 1,
        here: here && current?.sectionId === section.id,
        context: false,
        target: { projectId: project.id, sectionId: section.id, lifeSpace },
        place: `${project.name} / ${section.title}`,
      })
    }
  })
  return rows
}

/** Die Zahl je Lebensbereich: ohne Suche seine Projekte, mit Suche die getroffenen (selbst oder über einen Abschnitt). */
export function moveCounts(targets: MoveTargets, query: string): Record<LifeSpace, number> {
  const count = (lifeSpace: LifeSpace) => {
    const rows = moveRows(targets, lifeSpace, query, null)
    const withSection = new Set(rows.filter(row => row.kind === 'section').map(row => row.project.id))
    return rows.filter(row => row.kind === 'project' && (!row.context || withSection.has(row.project.id))).length
  }
  return { personal: count('personal'), work: count('work'), home: count('home') }
}

const sameName = (text: string) => normalizeSearchText(text).trim().replace(/\s+/g, ' ')

/**
 * „Neues Projekt „…“ in <Lebensbereich>“ (entschieden 05.10.2026): sobald getippt ist und im
 * gewählten Lebensbereich kein Projekt genau so heißt — auch wenn es Treffer gibt („Steuer“ neben
 * „Steuer 2026“).
 */
export function offersNewProject(targets: MoveTargets, lifeSpace: LifeSpace, query: string): boolean {
  const name = sameName(query)
  if (!name) return false
  return !(targets.get(lifeSpace) ?? []).some(({ project }) => sameName(project.name) === name)
}
